import React, { useState } from 'react';
import { Button } from '../ui';

/**
 * Invoice status options
 */
const INVOICE_STATUSES = {
  draft: { label: 'Draft', color: '#6080a0', bg: 'rgba(96,128,160,0.15)' },
  sent: { label: 'Sent', color: '#f0a030', bg: 'rgba(240,160,48,0.15)' },
  paid: { label: 'Paid', color: '#40c060', bg: 'rgba(64,192,96,0.15)' },
};

/**
 * InvoiceList - Modal for viewing and managing project invoices
 */
const InvoiceList = ({
  isOpen,
  onClose,
  invoices = [],
  onNew,
  onEdit,
  onDuplicate,
  onDelete,
  onStatusChange,
  isMobile = false,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleDelete = (invoice) => {
    if (confirmDelete === invoice.id) {
      onDelete?.(invoice.id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(invoice.id);
      // Auto-clear confirmation after 3 seconds
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const handleDuplicate = (invoice) => {
    const duplicated = {
      ...invoice,
      id: undefined, // Will be regenerated
      invoiceNumber: undefined, // Will get new number
      name: `${invoice.name} (Copy)`,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onDuplicate?.(duplicated);
  };

  const cycleStatus = (invoice) => {
    const statuses = ['draft', 'sent', 'paid'];
    const currentIndex = statuses.indexOf(invoice.status || 'draft');
    const nextStatus = statuses[(currentIndex + 1) % statuses.length];
    onStatusChange?.(invoice.id, nextStatus);
  };

  // Check if due date is past
  const isOverdue = (invoice) => {
    if (invoice.status === 'paid') return false;
    if (!invoice.dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(invoice.dueDate + 'T00:00:00');
    return due < today;
  };

  return (
    <div style={styles.overlay}>
      <div style={{
        ...styles.modal,
        width: isMobile ? '100%' : '750px',
        maxHeight: isMobile ? '100%' : '80vh',
        borderRadius: isMobile ? 0 : '12px',
      }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={{ fontSize: '20px' }}>🧾</span>
            <span style={styles.headerTitle}>Invoices</span>
            <span style={styles.count}>({invoices.length})</span>
          </div>
          <div style={styles.headerRight}>
            <Button
              variant="primary"
              onClick={onNew}
              style={{ background: '#0066cc', color: '#fff', fontWeight: '600' }}
            >
              + New Invoice
            </Button>
            <button style={styles.closeButton} onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {invoices.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🧾</div>
              <div style={styles.emptyTitle}>No Invoices Yet</div>
              <div style={styles.emptyText}>
                Create invoices from estimates or start a new blank invoice.
              </div>
              <Button
                variant="primary"
                onClick={onNew}
                style={{ marginTop: '16px', background: '#0066cc', color: '#fff' }}
              >
                Create First Invoice
              </Button>
            </div>
          ) : (
            <div style={styles.list}>
              {invoices.map((invoice) => {
                const status = INVOICE_STATUSES[invoice.status] || INVOICE_STATUSES.draft;
                const isDeleting = confirmDelete === invoice.id;
                const overdue = isOverdue(invoice);

                return (
                  <div key={invoice.id} style={{
                    ...styles.invoiceCard,
                    ...(overdue ? styles.overdueCard : {}),
                  }}>
                    <div style={styles.cardMain}>
                      {/* Left: Info */}
                      <div style={styles.cardInfo}>
                        <div style={styles.invoiceHeader}>
                          <span style={styles.invoiceNumber}>#{invoice.invoiceNumber}</span>
                          <span style={styles.invoiceName}>{invoice.name || 'Untitled Invoice'}</span>
                        </div>
                        <div style={styles.invoiceMeta}>
                          {invoice.clientName && (
                            <span style={styles.metaItem}>
                              <span style={styles.metaIcon}>👤</span>
                              {invoice.clientName}
                            </span>
                          )}
                          <span style={styles.metaItem}>
                            <span style={styles.metaIcon}>📅</span>
                            {formatDate(invoice.invoiceDate)}
                          </span>
                          <span style={{
                            ...styles.metaItem,
                            ...(overdue ? styles.overdueText : {}),
                          }}>
                            <span style={styles.metaIcon}>{overdue ? '⚠️' : '📆'}</span>
                            Due: {formatDate(invoice.dueDate)}
                          </span>
                        </div>
                      </div>

                      {/* Middle: Total */}
                      <div style={styles.cardTotal}>
                        <div style={styles.totalLabel}>Amount Due</div>
                        <div style={styles.totalAmount}>{formatCurrency(invoice.total)}</div>
                      </div>

                      {/* Right: Status */}
                      <button
                        style={{
                          ...styles.statusBadge,
                          background: status.bg,
                          color: status.color,
                          borderColor: status.color,
                        }}
                        onClick={() => cycleStatus(invoice)}
                        title="Click to change status"
                      >
                        {status.label}
                      </button>
                    </div>

                    {/* Actions */}
                    <div style={styles.cardActions}>
                      <button
                        style={styles.actionButton}
                        onClick={() => onEdit?.(invoice)}
                        title="Edit invoice"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        style={styles.actionButton}
                        onClick={() => handleDuplicate(invoice)}
                        title="Create a copy"
                      >
                        📄 Duplicate
                      </button>
                      <button
                        style={{
                          ...styles.actionButton,
                          ...styles.deleteButton,
                          ...(isDeleting ? styles.deleteConfirm : {}),
                        }}
                        onClick={() => handleDelete(invoice)}
                        title={isDeleting ? 'Click again to confirm' : 'Delete invoice'}
                      >
                        {isDeleting ? '⚠️ Confirm?' : '🗑️ Delete'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.footerInfo}>
            {invoices.length > 0 && (
              <>
                <span>Total invoices: {invoices.length}</span>
                <span style={styles.footerDivider}>|</span>
                <span>
                  Outstanding: {formatCurrency(
                    invoices
                      .filter(i => i.status !== 'paid')
                      .reduce((sum, i) => sum + (i.total || 0), 0)
                  )}
                </span>
                <span style={styles.footerDivider}>|</span>
                <span style={{ color: '#40c060' }}>
                  Paid: {formatCurrency(
                    invoices
                      .filter(i => i.status === 'paid')
                      .reduce((sum, i) => sum + (i.total || 0), 0)
                  )}
                </span>
              </>
            )}
          </div>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: '#1a2332',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,102,204,0.1)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerTitle: {
    color: '#0088ff',
    fontSize: '16px',
    fontWeight: '600',
  },
  count: {
    color: '#6080a0',
    fontSize: '14px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  closeButton: {
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    color: '#ff6666',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '4px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  emptyTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  emptyText: {
    color: '#6080a0',
    fontSize: '14px',
    maxWidth: '300px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  invoiceCard: {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  overdueCard: {
    borderColor: 'rgba(255,100,100,0.3)',
    background: 'rgba(255,100,100,0.05)',
  },
  cardMain: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    gap: '16px',
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
  },
  invoiceHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '6px',
  },
  invoiceNumber: {
    color: '#0088ff',
    fontSize: '14px',
    fontWeight: '700',
    background: 'rgba(0,102,204,0.15)',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  invoiceName: {
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  invoiceMeta: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#6080a0',
    fontSize: '12px',
  },
  metaIcon: {
    fontSize: '11px',
  },
  overdueText: {
    color: '#ff6060',
    fontWeight: '500',
  },
  cardTotal: {
    textAlign: 'right',
    minWidth: '120px',
  },
  totalLabel: {
    color: '#6080a0',
    fontSize: '10px',
    textTransform: 'uppercase',
    marginBottom: '2px',
  },
  totalAmount: {
    color: '#0088ff',
    fontSize: '18px',
    fontWeight: '700',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1px solid',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  cardActions: {
    display: 'flex',
    gap: '1px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    background: 'rgba(0,0,0,0.2)',
  },
  actionButton: {
    flex: 1,
    padding: '10px',
    background: 'transparent',
    border: 'none',
    color: '#6080a0',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  deleteButton: {
    color: '#a06060',
  },
  deleteConfirm: {
    background: 'rgba(255,80,80,0.2)',
    color: '#ff6060',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.2)',
  },
  footerInfo: {
    color: '#6080a0',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  footerDivider: {
    opacity: 0.5,
  },
};

export default InvoiceList;
